import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useApp } from "@/contexts/app-context";
import { createCompleteTemplate } from "@/services/supabase/templates";
import { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Check, ChevronDown, ChevronUp, LayoutTemplate, Loader2, Layers } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const tryParseJSON = (jsonString: string) => {
    try { return JSON.parse(jsonString); } catch { return null; }
};

const FIELD_TYPE_COLORS: Record<string, string> = {
    text: "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800/40",
    textarea: "bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-800/40",
    number: "bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-800/40",
    dropdown: "bg-green-50 text-green-600 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800/40",
    checkbox: "bg-teal-50 text-teal-600 border-teal-200 dark:bg-teal-950/30 dark:text-teal-400 dark:border-teal-800/40",
    date: "bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-800/40",
};

export const CreateTemplateTool: ToolCallMessagePartComponent = ({ result }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isCreated, setIsCreated] = useState(false);
    const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(0);
    const { projectId } = useApp();

    const isLoading = result === undefined;

    if (isLoading) {
        return (
            <div className="flex items-center gap-2.5 my-3 px-1 text-sm text-muted-foreground">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                    <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                </div>
                <span>Generating template structure…</span>
            </div>
        );
    }

    const rawResult = typeof result === 'string' ? tryParseJSON(result) : result;
    if (!rawResult) return null;

    // Unwrap tool result wrapper: {success, message, data: {...}} → data
    const templateData = (rawResult && typeof rawResult === 'object' && 'data' in rawResult && rawResult.data && typeof rawResult.data === 'object')
        ? rawResult.data
        : rawResult;

    const sections = templateData.sections || [];
    const hasSections = Array.isArray(sections) && sections.length > 0;
    const templateName = typeof templateData.templateName === 'object'
        ? JSON.stringify(templateData.templateName)
        : (templateData.templateName || "Generated Template");
    const templateDescription = typeof templateData.templateDescription === 'object'
        ? JSON.stringify(templateData.templateDescription)
        : templateData.templateDescription;

    const totalFields = sections.reduce((acc: number, s: any) => acc + (s.fields?.length || 0), 0);

    const handleCreate = async () => {
        if (!projectId) { toast.error("Project ID not found."); return; }
        const pid = Number(projectId);
        if (isNaN(pid)) { toast.error("Invalid Project ID."); return; }
        setIsCreating(true);
        try {
            await createCompleteTemplate({
                name: templateData.templateName,
                description: templateData.templateDescription,
                project_id: pid,
                is_public: true,
                sections: sections.map((s: any) => ({
                    name: s.name,
                    order_position: s.order_position,
                    fields: s.fields?.map((f: any) => ({
                        field_name: f.field_name,
                        field_type: (f.field_type || "text").toLowerCase(),
                        is_required: f.is_required,
                        default_value: f.default_value,
                        options: f.options,
                        validation_rules: f.validation_rules,
                        order_position: f.order_position
                    }))
                }))
            });
            setIsCreated(true);
            toast.success("Template created successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Failed to create template.");
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="my-3 rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            {/* Header */}
            <div
                className="flex items-center gap-3 px-4 py-3.5 cursor-pointer select-none bg-muted/40 hover:bg-muted/60 transition-colors border-b border-border"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <LayoutTemplate className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-foreground truncate">{templateName}</p>
                        {hasSections && (
                            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 rounded-sm font-medium shrink-0">
                                {sections.length} sections
                            </Badge>
                        )}
                    </div>
                    {templateDescription && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{templateDescription}</p>
                    )}
                </div>
                <div className="text-muted-foreground/60">
                    {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </div>
            </div>

            {!isCollapsed && (
                <>
                    {/* Summary strip */}
                    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border/60 bg-muted/20">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Layers className="h-3.5 w-3.5" />
                            <span>{sections.length} section{sections.length !== 1 ? 's' : ''}</span>
                        </div>
                        <span className="text-border">·</span>
                        <span className="text-xs text-muted-foreground">{totalFields} field{totalFields !== 1 ? 's' : ''}</span>
                    </div>

                    {/* Sections */}
                    {hasSections ? (
                        <div className="divide-y divide-border/60">
                            {sections.map((section: any, sIdx: number) => {
                                const isOpen = openSectionIndex === sIdx;
                                const fieldCount = section.fields?.length || 0;
                                return (
                                    <div key={sIdx}>
                                        {/* Section header — clickable */}
                                        <button
                                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                                            onClick={() => setOpenSectionIndex(isOpen ? null : sIdx)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{section.name}</span>
                                                <Badge variant="secondary" className="text-[10px] h-4 px-1.5 rounded-sm font-medium">
                                                    {fieldCount}
                                                </Badge>
                                            </div>
                                            <ChevronDown className={cn(
                                                "h-3.5 w-3.5 text-muted-foreground/60 transition-transform duration-150",
                                                isOpen && "rotate-180"
                                            )} />
                                        </button>

                                        {/* Fields */}
                                        {isOpen && section.fields?.length > 0 && (
                                            <div className="border-t border-border/40 divide-y divide-border/30">
                                                {section.fields.map((field: any, fIdx: number) => {
                                                    const typeKey = (field.field_type || 'text').toLowerCase();
                                                    const typeColor = FIELD_TYPE_COLORS[typeKey] || FIELD_TYPE_COLORS.text;
                                                    return (
                                                        <div
                                                            key={fIdx}
                                                            className="flex items-center justify-between pl-6 pr-4 py-2 hover:bg-muted/20 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm text-foreground/70 font-normal">{field.field_name}</span>
                                                                {field.is_required && (
                                                                    <span className="text-[9px] font-bold text-destructive uppercase tracking-wide">Required</span>
                                                                )}
                                                            </div>
                                                            <span className={cn(
                                                                "text-[10px] font-medium px-2 py-0.5 rounded-md border capitalize shrink-0",
                                                                typeColor
                                                            )}>
                                                                {typeKey}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="px-4 py-6 text-center text-muted-foreground text-sm">
                            No sections defined.
                        </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                        <p className="text-[11px] text-muted-foreground/60">{totalFields} fields across {sections.length} sections</p>
                        <div className="flex gap-2">
                            {isCreated ? (
                                <Button disabled variant="outline" size="sm" className="h-8 gap-1.5 text-green-600 border-green-200 bg-green-50 dark:bg-green-950/20">
                                    <Check className="h-3.5 w-3.5" />
                                    Created
                                </Button>
                            ) : (
                                <>
                                    <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-foreground" onClick={() => setIsCollapsed(true)}>
                                        Dismiss
                                    </Button>
                                    <Button size="sm" className="h-8" onClick={handleCreate} disabled={isCreating}>
                                        {isCreating && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                                        Create Template
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
